import { useEffect, useRef, useState } from "react";
import { usePage } from "@inertiajs/react";
import ChatbotFileList from "@/components/chatbot-file-list";
import { loadChatbotOrganizationFiles, SUPPORTED_FILE_ACCEPT, toOptimisticFile } from "@/components/chatbot-api";

const REASONING_OPTIONS = [
  { value: "low", label: "Instant" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "xhigh", label: "Extra high" },
  { value: "max", label: "Pro" }
];

function readStoredReasoningEffort(storageKey) {
  if (!storageKey) return "low";

  try {
    const storedEffort = globalThis.localStorage?.getItem(storageKey);
    return REASONING_OPTIONS.some((option) => option.value === storedEffort) ? storedEffort : "low";
  } catch (_error) {
    return "low";
  }
}

function writeStoredReasoningEffort(storageKey, effort) {
  if (!storageKey) return;

  try {
    globalThis.localStorage?.setItem(storageKey, effort);
  } catch (_error) {
    // The selection still works when browser storage is unavailable.
  }
}

function createClientRequestId() {
  return globalThis.crypto.randomUUID();
}

// onSubmit resolves to true when the composer state can be cleared, and false when it must be retained.
export default function ChatbotComposer({
  surface,
  question,
  setQuestion,
  processing,
  canStop = false,
  stopping = false,
  onStop,
  onSubmit,
  onError,
  autoFocus = false
}) {
  const { current_user: currentUser, max_file_size: maxFileSize } = usePage().props;
  const prefix = surface === "widget" ? "chatbot-widget" : "chatbot-fullscreen";
  const reasoningStorageKey = currentUser?.super_admin && currentUser?.email
    ? `obsero.chatbot.reasoning_effort.${currentUser.email}`
    : null;
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploads, setUploads] = useState([]);
  const [organizationFiles, setOrganizationFiles] = useState([]);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [organizationPickerOpen, setOrganizationPickerOpen] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [search, setSearch] = useState("");
  const [clientRequestId, setClientRequestId] = useState(null);
  const [reasoningEffort, setReasoningEffort] = useState(() => readStoredReasoningEffort(reasoningStorageKey));
  const [reasoningMenuOpen, setReasoningMenuOpen] = useState(false);
  const [maxMode, setMaxMode] = useState(false);

  useEffect(() => {
    setReasoningEffort(readStoredReasoningEffort(reasoningStorageKey));
  }, [reasoningStorageKey]);

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  }, [question]);

  // Opening a chat - new or existing - should put the cursor straight in the
  // textarea. The composer is remounted per chat by its key, so this runs once
  // per chat. preventScroll keeps it from fighting the messages autoscroll.
  useEffect(() => {
    if (!autoFocus) return;

    textareaRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  const openOrganizationPicker = async () => {
    setOrganizationPickerOpen(true);
    if (availableFiles.length > 0 || loadingFiles) return;

    setLoadingFiles(true);
    try {
      setAvailableFiles(await loadChatbotOrganizationFiles());
    } catch (error) {
      onError(error.message);
    } finally {
      setLoadingFiles(false);
    }
  };

  const addUploads = (event) => {
    const selected = Array.from(event.target.files || []);
    const oversized = selected.find((file) => file.size > maxFileSize);
    if (oversized) {
      onError(`${oversized.name} er større end ${maxFileSize / (1024 * 1024)} MB.`);
      event.target.value = "";
      return;
    }

    setUploads((current) => [...current, ...selected]);
    setClientRequestId(null);
    setMenuOpen(false);
    event.target.value = "";
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!question.trim() || processing) return;

    const requestId = clientRequestId || createClientRequestId();
    setClientRequestId(requestId);
    setReasoningMenuOpen(false);
    const succeeded = await onSubmit({
      question: question.trim(),
      uploads,
      organizationFiles,
      clientRequestId: requestId,
      // MAX pins the model and forces max effort server-side, so the effort
      // selector is not also sent - it would read as a choice that had an
      // effect.
      reasoningEffort: currentUser?.super_admin && !maxMode ? reasoningEffort : null,
      mode: currentUser?.super_admin && maxMode ? "max" : "dynamic"
    });
    if (succeeded) {
      setUploads([]);
      setOrganizationFiles([]);
      setMenuOpen(false);
      setOrganizationPickerOpen(false);
      setClientRequestId(null);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    submit(event);
  };

  const focusTextarea = (event) => {
    if (event.target.closest("button, a, input")) return;
    textareaRef.current?.focus();
  };

  const selectedFiles = [
    ...uploads.map(toOptimisticFile),
    ...organizationFiles.map(toOptimisticFile)
  ];
  const filteredFiles = availableFiles.filter((file) =>
    file.virtual_path.toLocaleLowerCase("da-DK").includes(search.toLocaleLowerCase("da-DK"))
  );
  const selectedReasoning = REASONING_OPTIONS.find((option) => option.value === reasoningEffort);

  return (
    <form className={`${prefix}__composer`} onSubmit={submit}>
      <ChatbotFileList
        files={selectedFiles}
        compact
        onRemove={(file) => {
          if (file.source === "organization_file") {
            setOrganizationFiles((current) => current.filter((candidate) => candidate.id !== file.id));
          } else {
            setUploads((current) => current.filter((candidate) => candidate !== file.fileObject));
          }
          setClientRequestId(null);
        }}
      />

      <div
        className={`${prefix}__composer-box ${currentUser?.super_admin ? "has-reasoning" : ""}`.trim()}
        onClick={focusTextarea}
      >
        <button
          type="button"
          className="chatbot-composer__attach-button"
          aria-label="Vedhæft fil"
          aria-expanded={menuOpen}
          disabled={processing}
          onClick={() => setMenuOpen((current) => !current)}
        >
          <i className="bi bi-paperclip" aria-hidden="true" />
        </button>
        <input ref={fileInputRef} type="file" accept={SUPPORTED_FILE_ACCEPT} multiple hidden onChange={addUploads} />

        {menuOpen ? (
          <div className="chatbot-composer__menu">
            {!organizationPickerOpen ? (
              <>
                <button type="button" onClick={() => fileInputRef.current?.click()}>
                  <i className="bi bi-upload" aria-hidden="true" /> Upload fil
                </button>
                <button type="button" onClick={openOrganizationPicker}>
                  <i className="bi bi-folder2-open" aria-hidden="true" /> Vælg organisationsfil
                </button>
              </>
            ) : (
              <div className="chatbot-composer__organization-picker">
                <div className="chatbot-composer__picker-header">
                  <button className="chatbot-composer__picker-back" type="button" aria-label="Tilbage" onClick={() => setOrganizationPickerOpen(false)}>
                    <i className="bi bi-arrow-left" aria-hidden="true" />
                  </button>
                  <strong>Organisationsfiler</strong>
                  <button
                    className="chatbot-composer__picker-done"
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setOrganizationPickerOpen(false);
                    }}
                  >
                    Færdig
                  </button>
                </div>
                <input
                  className="form-control form-control-sm"
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Søg efter fil"
                />
                <div className="chatbot-composer__file-options">
                  {loadingFiles ? <span className="text-secondary small">Henter filer...</span> : null}
                  {!loadingFiles && filteredFiles.length === 0 ? <span className="text-secondary small">Ingen filer fundet.</span> : null}
                  {filteredFiles.map((file) => {
                    const selected = organizationFiles.some((candidate) => candidate.id === file.id);
                    return (
                      <button
                        type="button"
                        key={file.id}
                        className={selected ? "is-selected" : ""}
                        onClick={() => {
                          setOrganizationFiles((current) => selected
                            ? current.filter((candidate) => candidate.id !== file.id)
                            : [...current, file]);
                          setClientRequestId(null);
                        }}
                      >
                        <i className={`bi ${selected ? "bi-check-circle-fill" : "bi-file-earmark"}`} aria-hidden="true" />
                        <span>{file.virtual_path}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : null}

        <textarea
          ref={textareaRef}
          className={`${prefix}__composer-input`}
          rows={1}
          value={question}
          onChange={(event) => {
            setQuestion(event.target.value);
            setClientRequestId(null);
          }}
          onKeyDown={handleKeyDown}
          placeholder={surface === "widget" ? "Spørg mig om DORA og NIS2" : "Skriv en besked"}
          required
        />
        {currentUser?.super_admin ? (
          <div className="chatbot-composer__reasoning">
            <button
              type="button"
              className="chatbot-composer__reasoning-trigger"
              aria-label="Vælg tænkeniveau"
              aria-expanded={reasoningMenuOpen}
              disabled={processing || maxMode}
              onClick={() => setReasoningMenuOpen((current) => !current)}
            >
              <i className="bi bi-stars" aria-hidden="true" />
              <span>{selectedReasoning.label}</span>
              <i className={`bi bi-chevron-${reasoningMenuOpen ? "up" : "down"}`} aria-hidden="true" />
            </button>

            {reasoningMenuOpen ? (
              <div className="chatbot-composer__reasoning-menu">
                {REASONING_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={option.value === reasoningEffort ? "is-selected" : ""}
                    aria-pressed={option.value === reasoningEffort}
                    onClick={() => {
                      setReasoningEffort(option.value);
                      writeStoredReasoningEffort(reasoningStorageKey, option.value);
                      setReasoningMenuOpen(false);
                      setClientRequestId(null);
                    }}
                  >
                    <strong>{option.label}</strong>
                    {option.value === reasoningEffort ? <i className="bi bi-check-lg" aria-hidden="true" /> : null}
                  </button>
                ))}
              </div>
            ) : null}

            <button
              type="button"
              className={`chatbot-composer__max-toggle ${maxMode ? "is-active" : ""}`.trim()}
              aria-pressed={maxMode}
              aria-label="Slå MAX-tilstand til eller fra"
              title="MAX: bedste model, maksimal tænkning og mere kontekst"
              disabled={processing}
              onClick={() => {
                setMaxMode((current) => !current);
                setReasoningMenuOpen(false);
                setClientRequestId(null);
              }}
            >
              MAX
            </button>
          </div>
        ) : null}
        <button
          type={processing ? "button" : "submit"}
          className={`${prefix}__send-button`}
          disabled={processing ? !canStop : !question.trim()}
          onClick={processing ? onStop : undefined}
          aria-label={processing ? (stopping ? "Stopper svar" : "Stop svar") : "Send besked"}
          title={processing ? "Stop svar" : "Send besked"}
        >
          {processing && !canStop ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          ) : (
            <i className={`bi ${processing ? "bi-stop-fill" : "bi-arrow-up"}`} aria-hidden="true" />
          )}
        </button>
      </div>
    </form>
  );
}
